import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import type { Client } from "@/models/Dashboards";
import FormModal from "../common/HPFormModal";
import {
  Box,
  Alert,
  FormLabel,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import HPDropdown from "../common/Dropdown";
import HPInput from "../common/Input";
import { useChartModal } from "@/hooks/use-chart-modal";
import { QuestionsList } from "./chart-modal/QuestionsList";
import DatasetSelector from "./chart-modal/DatasetSelector";
import RollupBuilder from "./chart-modal/RollupBuilder";
import type { LineFocusMode } from "@/store/slices/line-chart-config-slice";
import type { WidgetRollup } from "@/models/Rollup";
// import { AutoUpdateToggle } from "./chart-modal/AutoUpdateToggle";

interface CreateChartModalProps {
  action: (
    id: string,
    datasetId: string,
    datasetVersionId?: number | null,
    focusParams?: {
      focusMode: string;
      focusEntity: string;
      focusEntityDisplayLabel?: string;
      questionType?: string;
      focusRollup?: WidgetRollup;
      focusRollupName?: string;
    },
    timeUnit?: string,
  ) => void;
  initialValue?: string;
  chartType: "pie" | "bar" | "grouped_bar" | "line" | "donut" | "table" | "crosstab";
  isCreatingChart?: boolean;
  hasActiveFilters?: boolean;
  existingDatasetIds?: string[];
  primaryClient?: Client;
  additionalClients?: Client[];
  dashboardId?: string;
}

const CreateChartModal: React.FC<CreateChartModalProps> = ({
  action,
  chartType,
  isCreatingChart = false,
  hasActiveFilters = false,
  existingDatasetIds = [],
  primaryClient,
  additionalClients = [],
  dashboardId,
}) => {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  const {
    datasetId,
    datasetVersionId,
    searchTerm,
    selectedQuestionId,
    selectedQuestionType,
    autoUpdate,
    datasetGroups,
    isAutoSelected,
    filteredQuestions,
    recentQuestionIds,
    isFetching,
    hasMore,
    observerTarget,
    isDatasetRestricted,
    datasetSearchTerm,
    datasetsHasMore,
    handleDatasetSearchChange,
    isFetchingDatasets,
    observerTargetDatasets,
    handleDatasetChange,
    handleQuestionChange,
    handleQuestionSelect,
    setAutoUpdate,
    // Line chart focus mode state
    selectedQuestionDetails,
    focusMode,
    selectedVariableId,
    selectedValueId,
    selectedVariableLabel,
    selectedValueValue,
    timeUnit,
    enabledTimeUnits,
    needsFocusSelection,
    handleFocusModeChange,
    handleVariableSelect,
    handleValueSelect,
    handleTimeUnitChange,
    focusMetric,
    handleFocusMetricChange,
    rollupId,
    rollupName,
    rollupSelectedLabels,
    isRollupValid,
    handleRollupNameChange,
    handleRollupLabelsChange,
    isFetchingQuestionDetails,
  } = useChartModal(chartType, { hasActiveFilters, existingDatasetIds, primaryClient, additionalClients, dashboardId });

  const handleCreate = () => {
    // For crosstab, we don't need a question - just dataset
    // DatasetId and datasetVersionId are now saved by useChartCreation hook when chart is created
    if (chartType === "crosstab") {
      action("", datasetId, datasetVersionId);
    } else if (chartType === "line") {
      // For line charts, always pass timeUnit
      if (needsFocusSelection) {
        if (focusMetric === "rollup") {
          action(
            selectedQuestionId,
            datasetId,
            datasetVersionId,
            {
              focusMode: (focusMode || "variable").toUpperCase(),
              focusEntity: "",
              focusRollup: {
                id: rollupId,
                calculationType: "SUM_SELECTED",
                sourceCategoryLabels: rollupSelectedLabels,
              },
              focusRollupName: rollupName,
              questionType: selectedQuestionType ?? undefined,
            },
            timeUnit,
          );
          return;
        }

        // With focus selection, pass focus parameters
        const currentFocusMode = focusMode || "variable";

        // Get the display label from the dropdown
        let focusEntityDisplayLabel = "";
        if (currentFocusMode === "variable") {
          const variable = allVariables.find(
            (v) => v.id === selectedVariableId,
          );
          focusEntityDisplayLabel = variable?.rowtitle || variable?.title || "";
        } else {
          const valueObj = allValues.find((v) => v.id === selectedValueId);
          focusEntityDisplayLabel =
            valueObj?.rowtitle || valueObj?.title || valueObj?.label || "";
        }

        const focusEntity =
          currentFocusMode === "variable"
            ? selectedVariableLabel
            : selectedValueValue;

        action(
          selectedQuestionId,
          datasetId,
          datasetVersionId,
          {
            focusMode: currentFocusMode.toUpperCase(),
            focusEntity: focusEntity || "",
            focusEntityDisplayLabel: focusEntityDisplayLabel,
            questionType: selectedQuestionType ?? undefined,
          },
          timeUnit,
        );
      } else {
        // Without focus selection, pass undefined for focusParams but include timeUnit
        action(
          selectedQuestionId,
          datasetId,
          datasetVersionId,
          { focusMode: "", focusEntity: "", questionType: selectedQuestionType ?? undefined },
          timeUnit,
        );
      }
    } else if (chartType === "grouped_bar") {
      // Grouped bar only needs the Group By level. No specific entity and no
      // rollup — focusEntity/focusRollup stay null. The backend focusMode is
      // inverted relative to the Group By label (VALUE puts variables on the
      // x-axis), matching the edit drawer so the selection stays consistent.
      const groupByLevel = focusMode || "variable";
      action(selectedQuestionId, datasetId, datasetVersionId, {
        focusMode: groupByLevel === "variable" ? "VALUE" : "VARIABLE",
        focusEntity: "",
      });
    } else if (chartType === "bar") {
      // For bar charts with focus mode
      if (needsFocusSelection) {
        if (focusMetric === "rollup") {
          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: (focusMode || "variable").toUpperCase(),
            focusEntity: "",
            focusRollup: {
              id: rollupId,
              calculationType: "SUM_SELECTED",
              sourceCategoryLabels: rollupSelectedLabels,
            },
            focusRollupName: rollupName,
          });
        } else {
          const currentFocusMode = focusMode || "variable";
          let focusEntityDisplayLabel = "";
          if (currentFocusMode === "variable") {
            const variable = allVariables.find(
              (v) => v.id === selectedVariableId,
            );
            focusEntityDisplayLabel = variable?.rowtitle || variable?.title || "";
          } else {
            const valueObj = allValues.find((v) => v.id === selectedValueId);
            focusEntityDisplayLabel =
              valueObj?.rowtitle || valueObj?.title || valueObj?.label || "";
          }
          const focusEntity =
            currentFocusMode === "variable"
              ? selectedVariableLabel
              : selectedValueValue;

          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: currentFocusMode.toUpperCase(),
            focusEntity: focusEntity || "",
            focusEntityDisplayLabel: focusEntityDisplayLabel,
          });
        }
      } else {
        // Without focus selection
        action(selectedQuestionId, datasetId, datasetVersionId);
      }
    } else {
      // For all other chart types
      action(selectedQuestionId, datasetId, datasetVersionId);
    }
  };

  // Check if create button should be disabled
  const isCreateDisabled = () => {
    if (chartType === "crosstab") {
      return !datasetId;
    }

    // Grouped bar only needs the focus level, which always has a default.
    if (chartType === "grouped_bar") {
      return !selectedQuestionId || !datasetId;
    }

    // For line and bar charts with focus mode selection needed
    if ((chartType === "line" || chartType === "bar") && needsFocusSelection) {
      if (focusMetric === "rollup") {
        if (!isRollupValid) return true;
      } else {
        const currentFocusMode = focusMode || "variable";
        if (currentFocusMode === "variable" && !selectedVariableId) return true;
        if (currentFocusMode === "value" && !selectedValueId) return true;
      }
    }

    return !selectedQuestionId || !datasetId;
  };

  // Get all variables from question details
  const allVariables = selectedQuestionDetails
    ? [
        ...(selectedQuestionDetails.rowVariables || []),
        ...(selectedQuestionDetails.columnVariables || []),
      ]
    : [];

  // Get all values from question details
  const allValues = selectedQuestionDetails?.values || [];

  return (
    <FormModal
      title={`New ${chartType} chart`}
      confirmButtonText="Create"
      isConfirmButtonDisabled={isCreateDisabled() || isFetchingQuestionDetails}
      loading={isCreatingChart || isFetchingQuestionDetails}
      onConfirm={handleCreate}
      onCancel={closeTopModal}
    >
      <Box className="flex flex-col gap-2">
        {/* <AutoUpdateToggle checked={autoUpdate} onChange={setAutoUpdate} /> */}

        {isDatasetRestricted && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Dataset selection is restricted to existing datasets because filters
            are currently applied to the dashboard. To add a chart from a
            different dataset, please reset the filters first.
          </Alert>
        )}

        <DatasetSelector
          datasetGroups={datasetGroups}
          selectedDatasetId={datasetId || ""}
          isAutoSelected={isAutoSelected}
          searchTerm={datasetSearchTerm}
          onSearchChange={handleDatasetSearchChange}
          isFetching={isFetchingDatasets}
          hasMore={datasetsHasMore}
          observerTarget={observerTargetDatasets}
          onSelect={(id) => handleDatasetChange(id)}
        />

        {/* Time Unit Selector for Line Charts */}
        {chartType === "line" && (
          <HPDropdown
            label="Time Unit*"
            value={timeUnit}
            onChange={(value) =>
              handleTimeUnitChange(
                value as "QUARTER" | "SEMESTER" | "MONTH" | "WEEK",
              )
            }
            placeholder="Select time unit"
            options={(
              [
                { value: "QUARTER", label: "Quarter" },
                { value: "SEMESTER", label: "Semester" },
                { value: "MONTH", label: "Month" },
                { value: "WEEK", label: "Week" },
              ] as const
            ).filter(
              (o) =>
                !enabledTimeUnits ||
                enabledTimeUnits.includes(
                  o.value as "QUARTER" | "SEMESTER" | "MONTH" | "WEEK",
                ),
            )}
          />
        )}

        {datasetId && chartType !== "crosstab" && (
          <>
            <HPInput
              label="Select Question*"
              value={searchTerm || ""}
              onChange={handleQuestionChange}
              placeholder="Search by label or title..."
            />

            {chartType === "grouped_bar" &&
            !isFetching &&
            filteredQuestions.length === 0 ? (
              <Alert severity="info">
                Grouped Bar Charts require a Single question with multiple
                variables and multiple values.
              </Alert>
            ) : (
              <QuestionsList
                questions={filteredQuestions}
                selectedQuestionId={selectedQuestionId}
                onQuestionSelect={handleQuestionSelect}
                searchTerm={searchTerm}
                isFetching={isFetching}
                hasMore={hasMore}
                observerTarget={observerTarget}
                recentQuestionIds={recentQuestionIds}
              />
            )}

            {/* Line/Bar Chart Focus Mode Selection */}
            {needsFocusSelection && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {chartType === "grouped_bar"
                    ? "Group By*"
                    : "Focus Selection*"}
                </Typography>
                {chartType !== "grouped_bar" && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    This question has multiple variables. Choose how to display
                    the data:
                  </Typography>
                )}

                <RadioGroup
                  value={focusMode || "variable"}
                  onChange={(e) =>
                    handleFocusModeChange(e.target.value as LineFocusMode)
                  }
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel
                    value="variable"
                    control={<Radio size="small" />}
                    label={
                      chartType === "grouped_bar"
                        ? "Variables (variables are groups, values are series)"
                        : chartType === "line"
                          ? "Focus on a specific variable (show all its values as lines)"
                          : "Focus on a specific variable (show all its values as bars)"
                    }
                  />
                  <FormControlLabel
                    value="value"
                    control={<Radio size="small" />}
                    label={
                      chartType === "grouped_bar"
                        ? "Values (values are groups, variables are series)"
                        : chartType === "line"
                          ? "Focus on a specific value (show it across all variables as lines)"
                          : "Focus on a specific value (show it across all variables as bars)"
                    }
                  />
                </RadioGroup>

                {chartType !== "grouped_bar" && (() => {
                  const activeCategories =
                    focusMode === "value" ? allValues : allVariables;
                  const showMetricToggle =
                    (chartType === "bar" || chartType === "line") &&
                    activeCategories.length >= 2;
                  return (
                    <>
                      {showMetricToggle && (
                        <Box sx={{ mt: 1 }}>
                          <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
                            Focus Metric
                          </FormLabel>
                          <RadioGroup
                            value={focusMetric}
                            onChange={(e) => handleFocusMetricChange(e.target.value as "single" | "rollup")}
                          >
                            <FormControlLabel
                              value="single"
                              control={<Radio size="small" />}
                              label="Single Category"
                            />
                            <FormControlLabel
                              value="rollup"
                              control={<Radio size="small" />}
                              label="Rollup"
                            />
                          </RadioGroup>
                        </Box>
                      )}

                      {focusMetric === "rollup" ? (
                        <Box sx={{ mt: 2 }}>
                          <RollupBuilder
                            focusMode={focusMode || "variable"}
                            allVariables={allVariables}
                            allValues={allValues}
                            rollupName={rollupName}
                            rollupSelectedLabels={rollupSelectedLabels}
                            onRollupNameChange={handleRollupNameChange}
                            onSelectedLabelsChange={handleRollupLabelsChange}
                          />
                        </Box>
                      ) : (
                        <>
                          {(focusMode === "variable" || !focusMode) && (
                            <Box sx={{ mt: 2 }}>
                              <HPDropdown
                                label="Select Variable*"
                                value={selectedVariableId || ""}
                                onChange={(value) => {
                                  const variable = allVariables.find(
                                    (v) => v.id === value,
                                  );
                                  handleVariableSelect(value, variable?.label);
                                }}
                                placeholder="Choose a variable"
                                options={allVariables.map((v) => ({
                                  value: v.id,
                                  label: v.rowtitle || v.title,
                                }))}
                              />
                            </Box>
                          )}

                          {focusMode === "value" && (
                            <Box sx={{ mt: 2 }}>
                              <HPDropdown
                                label="Select Value*"
                                value={selectedValueId || ""}
                                onChange={(value) => {
                                  const valueObj = allValues.find(
                                    (v) => v.id === value,
                                  );
                                  handleValueSelect(
                                    value,
                                    valueObj
                                      ? String(valueObj.value)
                                      : undefined,
                                  );
                                }}
                                placeholder="Choose a value"
                                options={allValues.map((v) => ({
                                  value: v.id,
                                  label: v.title || v.label,
                                }))}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </>
        )}
      </Box>
    </FormModal>
  );
};

export default CreateChartModal;
